import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  DatePicker, 
  Select, 
  Space, 
  Typography, 
  Tag, 
  InputNumber,
  Alert,
  Tooltip,
  Spin,
  Badge,
  Divider,
  Progress,
  Collapse
} from 'antd';
import { 
  PlusOutlined, 
  MinusOutlined, 
  DeleteOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import { 
  SCAFFOLD_CONFIGURATIONS,
  calculateMaterialRequirements,
  checkRequirementsAvailability
} from '../../utils/inventoryCalculations';
import { 
  checkMaterialAvailability, 
  getInventoryStats, 
  getOverlappingReservations,
  MATERIAL_TYPES 
} from '../../firebase/inventoryService';
import InventoryStatusSummary from './InventoryStatusSummary';
import './ScaffoldAvailabilitySelector.css';
import './InventoryStatusSummary.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const ScaffoldAvailabilitySelector = ({ 
  onSelectionChange = () => {},
  initialDateRange = null,
  compact = false 
}) => {
  const [selectedConfigurations, setSelectedConfigurations] = useState([]);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [availabilityStatus, setAvailabilityStatus] = useState('unknown'); // 'available', 'unavailable', 'unknown'
  const [availabilityDetails, setAvailabilityDetails] = useState({});
  const [materialShortages, setMaterialShortages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'available'
  
  // Material balance state
  const [inventoryStats, setInventoryStats] = useState({});
  const [materialBalances, setMaterialBalances] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Overlapping reservations state
  const [overlappingReservations, setOverlappingReservations] = useState([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);

  // Get all available scaffold configurations
  const scaffoldConfigurations = Object.values(SCAFFOLD_CONFIGURATIONS);

  // Load inventory stats
  const loadInventoryStats = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const stats = await getInventoryStats();
      setInventoryStats(stats);
    } catch (error) {
      console.error('Error loading inventory stats:', error);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Calculate material balances
  const calculateMaterialBalances = useCallback(() => {
    if (!inventoryStats.byMaterialType || selectedConfigurations.length === 0) {
      setMaterialBalances([]);
      return;
    }

    // Calculate total requirements from selected configurations
    const lineItems = selectedConfigurations.map(config => ({
      width: config.width,
      workingHeight: config.platformHeightInMeter.replace('m', '') === '2' ? 4 : 
                    parseInt(config.platformHeightInMeter.replace('m', '')) + 2,
      quantity: config.quantity
    }));

    const requirements = calculateMaterialRequirements(lineItems);
    
    // Create balance data for all materials
    const balances = Object.values(MATERIAL_TYPES).map(materialType => {
      const inventory = inventoryStats.byMaterialType[materialType] || {
        totalQuantity: 0,
        newQuantity: 0,
        usedQuantity: 0,
        damagedQuantity: 0,
        availableQuantity: 0
      };

      const required = requirements.requirements[materialType]?.totalQuantity || 0;
      const totalAvailable = inventory.availableQuantity;
      const remaining = totalAvailable - required;
      const consumptionPercent = totalAvailable > 0 ? Math.round((required / totalAvailable) * 100) : 0;

      // Determine status color
      let status = 'success';
      if (remaining < 0) {
        status = 'exception';
      } else if (consumptionPercent >= 90) {
        status = 'exception';
      } else if (consumptionPercent >= 70) {
        status = 'active';
      }

      return {
        materialType,
        inventory,
        required,
        remaining,
        consumptionPercent: Math.min(consumptionPercent, 100),
        status,
        hasRequirement: required > 0
      };
    });

    // Sort by consumption percentage (highest first), then by material type
    balances.sort((a, b) => {
      if (a.hasRequirement && !b.hasRequirement) return -1;
      if (!a.hasRequirement && b.hasRequirement) return 1;
      if (a.hasRequirement && b.hasRequirement) {
        return b.consumptionPercent - a.consumptionPercent;
      }
      return a.materialType.localeCompare(b.materialType);
    });

    setMaterialBalances(balances);
  }, [inventoryStats, selectedConfigurations]);

  // Load overlapping reservations when date range changes
  const loadOverlappingReservations = useCallback(async () => {
    if (!dateRange) {
      setOverlappingReservations([]);
      return;
    }

    setReservationsLoading(true);
    try {
      const reservations = await getOverlappingReservations(
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setOverlappingReservations(reservations);
    } catch (error) {
      console.error('Error loading overlapping reservations:', error);
      setOverlappingReservations([]);
    } finally {
      setReservationsLoading(false);
    }
  }, [dateRange]);

  // Load inventory stats on component mount
  useEffect(() => {
    loadInventoryStats();
  }, [loadInventoryStats]);

  // Load overlapping reservations when date range changes
  useEffect(() => {
    loadOverlappingReservations();
  }, [loadOverlappingReservations]);

  // Recalculate balances when configurations or inventory changes
  useEffect(() => {
    calculateMaterialBalances();
  }, [calculateMaterialBalances]);

  // Check availability whenever selections or date range changes
  const checkAvailability = useCallback(async () => {
    if (selectedConfigurations.length === 0 || !dateRange) {
      setAvailabilityStatus('unknown');
      setAvailabilityDetails({});
      setMaterialShortages([]);
      return;
    }

    setLoading(true);
    
    try {
      // Convert selected configurations to line items format
      const lineItems = selectedConfigurations.map(config => ({
        width: config.width,
        workingHeight: config.platformHeightInMeter.replace('m', '') === '2' ? 4 : 
                      parseInt(config.platformHeightInMeter.replace('m', '')) + 2, // Convert platform height to working height
        quantity: config.quantity
      }));

      // Calculate total material requirements
      const requirements = calculateMaterialRequirements(lineItems);
      
      // Check availability for all requirements
      const availability = await checkRequirementsAvailability(
        requirements.requirements,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );

      setAvailabilityStatus(availability.isAvailable ? 'available' : 'unavailable');
      setAvailabilityDetails(availability.availabilityResults);
      setMaterialShortages(availability.shortages);

      // Notify parent component
      onSelectionChange({
        configurations: selectedConfigurations,
        dateRange: {
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        },
        availability: availability,
        status: availability.isAvailable ? 'available' : 'unavailable'
      });

    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityStatus('unknown');
      setAvailabilityDetails({});
      setMaterialShortages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedConfigurations, dateRange, onSelectionChange]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  // Add a scaffold configuration
  const addConfiguration = (configId) => {
    const config = SCAFFOLD_CONFIGURATIONS[configId];
    if (!config) return;

    const existingIndex = selectedConfigurations.findIndex(c => c.id === configId);
    
    if (existingIndex >= 0) {
      // Increase quantity of existing configuration
      const updated = [...selectedConfigurations];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + 1
      };
      setSelectedConfigurations(updated);
    } else {
      // Add new configuration
      setSelectedConfigurations(prev => [...prev, {
        ...config,
        quantity: 1
      }]);
    }
  };

  // Update configuration quantity
  const updateQuantity = (configId, newQuantity) => {
    if (newQuantity <= 0) {
      removeConfiguration(configId);
      return;
    }

    setSelectedConfigurations(prev => 
      prev.map(config => 
        config.id === configId 
          ? { ...config, quantity: newQuantity }
          : config
      )
    );
  };

  // Remove a configuration
  const removeConfiguration = (configId) => {
    setSelectedConfigurations(prev => prev.filter(config => config.id !== configId));
  };

  // Clear all selections
  const clearAll = () => {
    setSelectedConfigurations([]);
  };

  // Get status color for cards
  const getStatusColor = () => {
    switch (availabilityStatus) {
      case 'available': return '#52c41a';
      case 'unavailable': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (availabilityStatus) {
      case 'available': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'unavailable': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default: return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  // State for dropdown selection
  const [selectedConfigId, setSelectedConfigId] = useState('');

  // Add configuration from dropdown
  const addConfigurationFromDropdown = () => {
    if (!selectedConfigId) return;
    
    addConfiguration(selectedConfigId);
    setSelectedConfigId(''); // Reset dropdown after adding
  };

  // Check if a configuration is available for the current date range
  const checkConfigurationAvailability = useCallback(async (config) => {
    if (!dateRange) return true; // If no date range, show all items
    
    try {
      // Convert configuration to line item format for availability checking
      const lineItem = {
        width: config.width,
        workingHeight: config.platformHeightInMeter.replace('m', '') === '2' ? 4 : 
                      parseInt(config.platformHeightInMeter.replace('m', '')) + 2,
        quantity: 1
      };

      // Calculate material requirements for this configuration
      const requirements = calculateMaterialRequirements([lineItem]);
      
      // Check availability for all requirements
      const availability = await checkRequirementsAvailability(
        requirements.requirements,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );

      return availability.isAvailable;
    } catch (error) {
      console.error('Error checking configuration availability:', error);
      return true; // Default to showing the item if check fails
    }
  }, [dateRange]);

  // State for tracking configuration availability
  const [configurationAvailability, setConfigurationAvailability] = useState({});
  const [availabilityCheckLoading, setAvailabilityCheckLoading] = useState(false);

  // Check availability for all configurations when filter type is 'available' and date range changes
  useEffect(() => {
    const checkAllConfigurationsAvailability = async () => {
      if (filterType !== 'available' || !dateRange) {
        setConfigurationAvailability({});
        return;
      }

      setAvailabilityCheckLoading(true);
      const availabilityMap = {};

      try {
        // Check availability for all configurations
        const availabilityPromises = scaffoldConfigurations.map(async (config) => {
          const isAvailable = await checkConfigurationAvailability(config);
          return { configId: config.id, isAvailable };
        });

        const results = await Promise.all(availabilityPromises);
        
        results.forEach(({ configId, isAvailable }) => {
          availabilityMap[configId] = isAvailable;
        });

        setConfigurationAvailability(availabilityMap);
      } catch (error) {
        console.error('Error checking configurations availability:', error);
        setConfigurationAvailability({});
      } finally {
        setAvailabilityCheckLoading(false);
      }
    };

    checkAllConfigurationsAvailability();
  }, [filterType, dateRange, checkConfigurationAvailability, scaffoldConfigurations]);

  // Get available configurations for dropdown (exclude already selected and apply filter)
  const getAvailableConfigurationsForDropdown = () => {
    const selectedIds = selectedConfigurations.map(c => c.id);
    let filteredConfigs = scaffoldConfigurations.filter(config => !selectedIds.includes(config.id));

    // Apply availability filter if 'available' is selected
    if (filterType === 'available' && dateRange) {
      filteredConfigs = filteredConfigs.filter(config => {
        const availability = configurationAvailability[config.id];
        return availability === true; // Only show configurations that are confirmed available
      });
    }

    return filteredConfigs;
  };

  // Render selected configuration card
  const renderSelectedConfigurationCard = (config) => {
    const quantity = config.quantity || 0;
    
    const cardStyle = {
      borderColor: getStatusColor(),
      borderWidth: 2,
      backgroundColor: availabilityStatus === 'available' ? '#f6ffed' : '#fff2f0'
    };

    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={config.id}>
        <Card 
          size="small" 
          className="scaffold-config-card selected-config-card"
          style={cardStyle}
        >
          <div className="config-header">
            <Text strong ellipsis title={config.name}>
              {config.name}
            </Text>
            <Badge 
              count={quantity} 
              style={{ backgroundColor: getStatusColor() }}
            />
          </div>
          
          <div className="config-details">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Tag color="blue" size="small">
                  {config.width} width
                </Tag>
                <Tag color="green" size="small">
                  {config.platformHeightInMeter} platform
                </Tag>
              </div>
              
              <div className="quantity-controls">
                <Space>
                  <Button 
                    size="small" 
                    icon={<MinusOutlined />}
                    onClick={() => updateQuantity(config.id, quantity - 1)}
                  />
                  <InputNumber
                    size="small"
                    min={0}
                    value={quantity}
                    onChange={(value) => updateQuantity(config.id, value || 0)}
                    style={{ width: 60 }}
                  />
                  <Button 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={() => updateQuantity(config.id, quantity + 1)}
                  />
                  <Button 
                    size="small" 
                    type="text" 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeConfiguration(config.id)}
                    title="Remove configuration"
                  />
                </Space>
              </div>
            </Space>
          </div>
        </Card>
      </Col>
    );
  };

  // Render overall status alert
  const renderStatusAlert = () => {
    if (selectedConfigurations.length === 0) {
      return (
        <Alert
          type="info"
          message="Select scaffold configurations to check availability"
          description="Choose your required scaffold types and quantities, then select a date range to check availability."
          icon={<InfoCircleOutlined />}
          showIcon
        />
      );
    }

    if (!dateRange) {
      return (
        <Alert
          type="warning"
          message="Select date range to check availability"
          description="Please select a rental period to check if the selected configurations are available."
          icon={<CalendarOutlined />}
          showIcon
        />
      );
    }

    if (loading) {
      return (
        <Alert
          type="info"
          message="Checking availability..."
          description="Verifying material availability for your selected configurations."
          icon={<Spin size="small" />}
          showIcon
        />
      );
    }

    const statusConfig = {
      available: {
        type: 'success',
        message: 'All configurations are available',
        description: `${selectedConfigurations.length} scaffold configuration(s) can be fulfilled for the selected date range.`
      },
      unavailable: {
        type: 'error',
        message: 'Insufficient materials available',
        description: `Some materials are not available in sufficient quantities. ${materialShortages.length} material shortage(s) detected.`
      },
      unknown: {
        type: 'info',
        message: 'Availability check required',
        description: 'Unable to determine availability. Please try again.'
      }
    };

    const config = statusConfig[availabilityStatus];
    
    return (
      <Alert
        type={config.type}
        message={config.message}
        description={config.description}
        icon={getStatusIcon()}
        showIcon
      />
    );
  };

  // Render material shortages
  const renderShortages = () => {
    if (materialShortages.length === 0) return null;

    return (
      <Card title="Material Shortages" size="small" className="shortages-card">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {materialShortages.map((shortage, index) => (
            <div key={index} className="shortage-item">
              <Space>
                <Text strong>{shortage.materialType}</Text>
                <Tag color="error">
                  Need {shortage.required}, Available {shortage.available}
                </Tag>
                <Tag color="warning">
                  Short by {shortage.shortage}
                </Tag>
              </Space>
            </div>
          ))}
        </Space>
      </Card>
    );
  };

  // Render material balance overview
  const renderMaterialBalanceOverview = () => {
    if (selectedConfigurations.length === 0) return null;

    return (
      <Card 
        title="Material Balance Overview" 
        className="material-balance-card"
        loading={balanceLoading}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {materialBalances.map((balance, index) => (
            <div key={balance.materialType} className="material-balance-item">
              <div className="balance-header">
                <Text strong>{balance.materialType}</Text>
                <Space size="small">
                  {balance.hasRequirement && (
                    <Tag color={balance.status === 'exception' ? 'error' : balance.status === 'active' ? 'warning' : 'success'}>
                      {balance.consumptionPercent}% used
                    </Tag>
                  )}
                  {balance.remaining < 0 && (
                    <Tag color="error">
                      Short by {Math.abs(balance.remaining)}
                    </Tag>
                  )}
                </Space>
              </div>
              
              <div className="balance-progress">
                <Progress
                  percent={balance.consumptionPercent}
                  status={balance.status}
                  size="small"
                  format={() => `${balance.required}/${balance.inventory.availableQuantity}`}
                />
              </div>
              
              <Collapse ghost size="small">
                <Collapse.Panel 
                  header={
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      View breakdown
                    </Text>
                  } 
                  key="1"
                >
                  <div className="balance-breakdown">
                    <Row gutter={16}>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">New:</Text>
                          <div>
                            <Text strong>{balance.inventory.newQuantity}</Text>
                            <Text type="secondary"> available</Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">Used:</Text>
                          <div>
                            <Text strong>{balance.inventory.usedQuantity}</Text>
                            <Text type="secondary"> available</Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">Damaged:</Text>
                          <div>
                            <Text strong style={{ color: '#ff4d4f' }}>{balance.inventory.damagedQuantity}</Text>
                            <Text type="secondary"> unavailable</Text>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '8px 0' }} />
                    <Row gutter={16}>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">Total Available:</Text>
                          <div>
                            <Text strong style={{ color: '#52c41a' }}>{balance.inventory.availableQuantity}</Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">Required:</Text>
                          <div>
                            <Text strong style={{ color: '#1890ff' }}>{balance.required}</Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="breakdown-item">
                          <Text type="secondary">Remaining:</Text>
                          <div>
                            <Text 
                              strong 
                              style={{ color: balance.remaining >= 0 ? '#52c41a' : '#ff4d4f' }}
                            >
                              {balance.remaining}
                            </Text>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Collapse.Panel>
              </Collapse>
            </div>
          ))}
          
          {materialBalances.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
              <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div>Select scaffold configurations to view material balance</div>
            </div>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div className="scaffold-availability-selector">
      <div className="selector-header">
        <Title level={4}>Scaffold Availability Checker</Title>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder={['Start Date', 'End Date']}
            style={{ width: 250 }}
          />
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 140 }}
          >
            <Option value="all">All Items</Option>
            <Option value="available">Available Only</Option>
          </Select>
          {selectedConfigurations.length > 0 && (
            <Button onClick={clearAll} type="text" danger>
              Clear All
            </Button>
          )}
        </Space>
      </div>

      {renderStatusAlert()}

      {/* Inventory Status Summary */}
      <InventoryStatusSummary
        dateRange={dateRange}
        inventoryStats={inventoryStats}
        overlappingReservations={overlappingReservations}
        loading={reservationsLoading || balanceLoading}
      />

      {materialShortages.length > 0 && renderShortages()}

      {/* Dropdown Selector for Adding Configurations */}
      <Card title="Add Scaffold Configuration" className="add-config-card">
        <Space.Compact style={{ width: '100%' }}>
          <Select
            value={selectedConfigId}
            onChange={setSelectedConfigId}
            placeholder={
              availabilityCheckLoading && filterType === 'available' 
                ? "Checking availability..." 
                : "Select scaffold configuration..."
            }
            style={{ flex: 1 }}
            showSearch
            optionFilterProp="children"
            loading={availabilityCheckLoading && filterType === 'available'}
            disabled={availabilityCheckLoading && filterType === 'available'}
            filterOption={(input, option) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getAvailableConfigurationsForDropdown().map(config => (
              <Option key={config.id} value={config.id}>
                {config.name} ({config.width} width, {config.platformHeightInMeter} platform)
                {filterType === 'available' && configurationAvailability[config.id] === true && (
                  <Tag color="success" size="small" style={{ marginLeft: 8 }}>
                    Available
                  </Tag>
                )}
              </Option>
            ))}
          </Select>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={addConfigurationFromDropdown}
            disabled={!selectedConfigId || (availabilityCheckLoading && filterType === 'available')}
          >
            Add
          </Button>
        </Space.Compact>
        
        {/* Loading message for availability check */}
        {availabilityCheckLoading && filterType === 'available' && (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <Spin size="small" />
            <Text type="secondary" style={{ marginLeft: 8 }}>
              Checking availability for all configurations...
            </Text>
          </div>
        )}
        
        {/* No configurations available message */}
        {!availabilityCheckLoading && filterType === 'available' && dateRange && 
         getAvailableConfigurationsForDropdown().length === 0 && selectedConfigurations.length === 0 && (
          <Alert
            type="warning"
            message="No available configurations"
            description="No scaffold configurations have sufficient materials available for the selected date range. Try selecting a different date range or switch to 'All Items' to see all configurations."
            style={{ marginTop: 8 }}
            showIcon
          />
        )}
        
        {/* All configurations selected message */}
        {getAvailableConfigurationsForDropdown().length === 0 && selectedConfigurations.length > 0 && (
          <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
            {filterType === 'available' 
              ? "All available configurations have been added. Remove existing ones to add different configurations."
              : "All configurations have been added. Remove existing ones to add different configurations."
            }
          </Text>
        )}
        
        {/* Filter explanation */}
        {filterType === 'available' && !dateRange && (
          <Alert
            type="info"
            message="Select a date range to filter available items"
            description="The 'Available Only' filter requires a date range to check material availability."
            style={{ marginTop: 8 }}
            showIcon
          />
        )}
      </Card>

      {/* Selected Configurations Cards */}
      {selectedConfigurations.length > 0 && (
        <Card title="Selected Configurations" className="selected-configurations-card">
          <Row gutter={[16, 16]}>
            {selectedConfigurations.map(renderSelectedConfigurationCard)}
          </Row>
        </Card>
      )}

      {/* Material Balance Overview */}
      {renderMaterialBalanceOverview()}
    </div>
  );
};

export default ScaffoldAvailabilitySelector;
